import { create } from 'zustand';
import type { Message, Conversation, Artifact, StreamMessage, InteractiveMode, ProfileProgress, PlanningSession } from '@/types';
import { chat, planning } from '@/lib/api';

interface ChatState {
  conversations: Conversation[];
  currentConversationId: string | null;
  messages: Message[];
  isLoading: boolean;
  isStreaming: boolean;
  streamingContent: string;
  streamingArtifacts: Artifact[];
  // Interactive options from stream
  pendingInteractive: InteractiveMode | null;
  // Profile progress from stream
  profilingProgress: ProfileProgress | null;
  // Evaluation data from stream
  pendingEvaluationId: string | null;
  pendingConfidenceScore: number | null;
  pendingConfidenceRationale: string | null;
  pendingCitations: Message['citations'] | null;
  thinkingStatus: string | null;
  error: string | null;

  // Planning mode state
  planningSession: PlanningSession | null;
  planningMode: boolean;
  planningNeedsAccountSelection: boolean;
  planningAvailableAccounts: Array<{ id: string; name: string }>;

  // Actions
  loadConversations: () => Promise<void>;
  createConversation: () => Promise<string>;
  selectConversation: (id: string) => Promise<void>;
  deleteConversation: (id: string) => Promise<void>;
  sendMessage: (content: string) => Promise<void>;
  addAssistantMessage: (content: string, artifacts?: Artifact[]) => Promise<void>;
  handleStreamMessage: (message: StreamMessage) => void;
  finalizeStream: () => void;
  clearError: () => void;

  // Planning mode actions
  startPlanningMode: () => Promise<void>;
  selectPlanningAccount: (accountId: string) => Promise<void>;
  approvePlan: () => Promise<void>;
  confirmExecution: (token: string, dryRun?: boolean) => Promise<void>;
  cancelPlanningSession: () => Promise<void>;
  refreshPlanningSession: () => Promise<void>;
}

export const useChatStore = create<ChatState>((set, get) => ({
  conversations: [],
  currentConversationId: null,
  messages: [],
  isLoading: false,
  isStreaming: false,
  streamingContent: '',
  streamingArtifacts: [],
  pendingInteractive: null,
  profilingProgress: null,
  pendingEvaluationId: null,
  pendingConfidenceScore: null,
  pendingConfidenceRationale: null,
  pendingCitations: null,
  thinkingStatus: null,
  error: null,

  // Planning mode initial state
  planningSession: null,
  planningMode: false,
  planningNeedsAccountSelection: false,
  planningAvailableAccounts: [],

  loadConversations: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await chat.listConvs({});
      set({ conversations: response.items as Conversation[] });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load conversations' });
    } finally {
      set({ isLoading: false });
    }
  },

  createConversation: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await chat.createConv({});
      const conversation = response.conversation as Conversation;
      set((state) => ({
        conversations: [conversation, ...state.conversations],
        currentConversationId: conversation.id,
        messages: [],
        // Reset planning state when creating new conversation
        planningSession: null,
        planningMode: false,
        planningNeedsAccountSelection: false,
        planningAvailableAccounts: [],
      }));
      return conversation.id;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to create conversation' });
      throw error;
    } finally {
      set({ isLoading: false });
    }
  },

  selectConversation: async (id: string) => {
    set({ isLoading: true, error: null, currentConversationId: id });
    try {
      const response = await chat.getConv(id);
      set({ messages: response.messages as Message[] });

      // Check for active planning session
      try {
        const planningResponse = await planning.getPlanningSession({ conversationId: id });
        if (planningResponse.session) {
          set({
            planningSession: planningResponse.session as PlanningSession,
            planningMode: !['completed', 'cancelled'].includes(planningResponse.session.state),
          });
        } else {
          set({
            planningSession: null,
            planningMode: false,
          });
        }
      } catch {
        // No planning session - that's fine
        set({
          planningSession: null,
          planningMode: false,
        });
      }
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to load conversation' });
    } finally {
      set({ isLoading: false });
    }
  },

  deleteConversation: async (id: string) => {
    set({ isLoading: true, error: null });
    try {
      await chat.deleteConv(id);
      set((state) => ({
        conversations: state.conversations.filter(c => c.id !== id),
        currentConversationId: state.currentConversationId === id ? null : state.currentConversationId,
        messages: state.currentConversationId === id ? [] : state.messages,
        // Reset planning state if deleting current conversation
        planningSession: state.currentConversationId === id ? null : state.planningSession,
        planningMode: state.currentConversationId === id ? false : state.planningMode,
      }));
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to delete conversation' });
    } finally {
      set({ isLoading: false });
    }
  },

  sendMessage: async (content: string) => {
    const { currentConversationId, createConversation } = get();

    let conversationId = currentConversationId;
    if (!conversationId) {
      conversationId = await createConversation();
    }

    // Add user message to the list immediately
    const userMessage: Message = {
      id: `temp_${Date.now()}`,
      conversationId,
      role: 'user',
      content,
      createdAt: new Date().toISOString(),
    };

    set((state) => ({
      messages: [...state.messages, userMessage],
      isStreaming: true,
      streamingContent: '',
      streamingArtifacts: [],
      error: null,
    }));

    try {
      // Use the generated Encore client for streaming
      const stream = await chat.streamChat({
        conversationId,
        message: content,
      });

      // Read messages from the stream
      for await (const message of stream) {
        get().handleStreamMessage(message);
      }

      // Check if planning mode was triggered
      const normalizedContent = content.toLowerCase().trim();
      if (normalizedContent === 'plan changes' || normalizedContent.includes('plan the changes')) {
        await get().refreshPlanningSession();
      }
    } catch (error) {
      set({
        isStreaming: false,
        error: error instanceof Error ? error.message : 'Failed to send message',
      });
    }
  },

  addAssistantMessage: async (content: string, artifacts?: Artifact[]) => {
    const { currentConversationId, createConversation } = get();

    let conversationId = currentConversationId;
    if (!conversationId) {
      conversationId = await createConversation();
    }

    const assistantMessage: Message = {
      id: `msg_${Date.now()}`,
      conversationId,
      role: 'assistant',
      content,
      artifacts,
      createdAt: new Date().toISOString(),
    };

    set((state) => ({
      messages: [...state.messages, assistantMessage],
    }));
  },

  handleStreamMessage: (message: StreamMessage) => {
    switch (message.type) {
      case 'token':
        if (message.content) {
          set((state) => ({
            streamingContent: state.streamingContent + message.content,
          }));
        }
        break;
      case 'artifact':
        if (message.artifact) {
          set((state) => ({
            streamingArtifacts: [...state.streamingArtifacts, message.artifact!],
          }));
        }
        break;
      case 'thinking':
        set({ thinkingStatus: message.content ?? null });
        break;
      case 'interactive':
        if (message.interactive) {
          set({ pendingInteractive: message.interactive });
        }
        break;
      case 'progress':
        if (message.progress) {
          set({ profilingProgress: message.progress });
        }
        break;
      case 'error':
        set({
          isStreaming: false,
          error: message.error ?? 'An error occurred',
        });
        break;
      case 'done':
        // Capture evaluation data before finalizing
        set({
          pendingEvaluationId: message.evaluationId ?? null,
          pendingConfidenceScore: message.confidenceScore ?? null,
          pendingConfidenceRationale: message.confidenceRationale ?? null,
          pendingCitations: message.citations ?? null,
        });
        get().finalizeStream();
        break;
    }
  },

  finalizeStream: () => {
    const {
      currentConversationId,
      streamingContent,
      streamingArtifacts,
      pendingInteractive,
      pendingEvaluationId,
      pendingConfidenceScore,
      pendingConfidenceRationale,
      pendingCitations,
    } = get();

    if (!currentConversationId) return;

    const assistantMessage: Message = {
      id: `msg_${Date.now()}`,
      conversationId: currentConversationId,
      role: 'assistant',
      content: streamingContent,
      artifacts: streamingArtifacts.length > 0 ? streamingArtifacts : undefined,
      interactive: pendingInteractive ?? undefined,
      citations: pendingCitations ?? undefined,
      evaluationId: pendingEvaluationId ?? undefined,
      confidenceScore: pendingConfidenceScore ?? undefined,
      confidenceRationale: pendingConfidenceRationale ?? undefined,
      createdAt: new Date().toISOString(),
    };

    set((state) => ({
      messages: [...state.messages, assistantMessage],
      isStreaming: false,
      streamingContent: '',
      streamingArtifacts: [],
      pendingInteractive: null,
      profilingProgress: null,
      thinkingStatus: null,
      pendingEvaluationId: null,
      pendingConfidenceScore: null,
      pendingConfidenceRationale: null,
      pendingCitations: null,
    }));

    // Update conversation in the list
    set((state) => ({
      conversations: state.conversations.map(c =>
        c.id === currentConversationId
          ? { ...c, lastMessage: streamingContent.slice(0, 100), updatedAt: new Date().toISOString() }
          : c
      ),
    }));
  },

  clearError: () => set({ error: null }),

  // Planning mode actions
  startPlanningMode: async () => {
    const { currentConversationId } = get();
    if (!currentConversationId) {
      set({ error: 'No conversation selected' });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const response = await planning.startPlanning({
        conversationId: currentConversationId,
      });

      set({
        planningSession: response.session as PlanningSession,
        planningMode: true,
        planningNeedsAccountSelection: response.needsAccountSelection ?? false,
        planningAvailableAccounts: response.availableAccounts ?? [],
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to start planning mode' });
    } finally {
      set({ isLoading: false });
    }
  },

  selectPlanningAccount: async (accountId: string) => {
    const { planningSession } = get();
    if (!planningSession) {
      set({ error: 'No active planning session' });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const response = await planning.selectAccount(planningSession.id, {
        accountId,
      });

      set({
        planningSession: response.session as PlanningSession,
        planningNeedsAccountSelection: false,
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to select account' });
    } finally {
      set({ isLoading: false });
    }
  },

  approvePlan: async () => {
    const { planningSession } = get();
    if (!planningSession) {
      set({ error: 'No active planning session' });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const response = await planning.approvePlan(planningSession.id);

      set({
        planningSession: response.session as PlanningSession,
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to approve plan' });
    } finally {
      set({ isLoading: false });
    }
  },

  confirmExecution: async (token: string, dryRun = true) => {
    const { planningSession } = get();
    if (!planningSession) {
      set({ error: 'No active planning session' });
      return;
    }

    set({ isLoading: true, error: null });
    try {
      const response = await planning.confirmExecution(planningSession.id, {
        confirmationToken: token,
        dryRun,
      });

      set({
        planningSession: response.session as PlanningSession,
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to confirm execution' });
    } finally {
      set({ isLoading: false });
    }
  },

  cancelPlanningSession: async () => {
    const { planningSession } = get();
    if (!planningSession) {
      return;
    }

    set({ isLoading: true, error: null });
    try {
      await planning.cancelPlanningSession(planningSession.id);

      set({
        planningSession: null,
        planningMode: false,
        planningNeedsAccountSelection: false,
        planningAvailableAccounts: [],
      });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'Failed to cancel planning session' });
    } finally {
      set({ isLoading: false });
    }
  },

  refreshPlanningSession: async () => {
    const { currentConversationId } = get();
    if (!currentConversationId) return;

    try {
      const response = await planning.getPlanningSession({
        conversationId: currentConversationId,
      });

      if (response.session) {
        set({
          planningSession: response.session as PlanningSession,
          planningMode: !['completed', 'cancelled'].includes(response.session.state),
        });
      }
    } catch {
      // Session not found - that's fine
    }
  },
}));
